import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { HiddenFile } from '@/components/molecules/common/Form';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';

interface UploadButtonProps {
  file: File | null;
  setFile: (f: File | null) => void;
}

const MAX_SIZE = 15; // 15MB

const UploadButton = ({ file, setFile }: UploadButtonProps) => {
  const { t } = useTranslation();

  const imageRef = useRef<HTMLInputElement>(null);

  const [error, setError] = useState('');

  const handleAddFile = (f: File | null) => {
    setError('');

    if (!f) return;
    if (f.size > MAX_SIZE * 1024 * 1024) {
      setError(`${t('modal.item_ask_more_info.file_error_max')} ${MAX_SIZE}MB`);
      return;
    }

    setFile(f);
  };

  if (file) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          border: '1px solid gray',
          borderStyle: 'dashed',
        }}
      >
        <Box sx={{ display: 'inline-grid', flexDirection: 'column', alignItems: 'center', gap: '5px', padding: 1 }}>
          <Typography
            variant="body1"
            sx={{ fontSize: '0.75rem' }}
            noWrap
          >
            {file.name}
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: '0.6rem' }}
            color="grey"
            noWrap
          >
            {file.size} KB
          </Typography>
        </Box>

        <IconButton onClick={() => setFile(null)}>
          <CloseIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <>
      <Stack>
        <Box
          component="button"
          sx={{ padding: 1, border: '1px solid gray', borderStyle: 'dashed', width: 48, height: 48 }}
          onClick={() => imageRef.current?.click()}
        >
          <AddOutlinedIcon sx={{ fontSize: 30 }} />
        </Box>

        <Typography
          variant="caption"
          color="gray"
          sx={{ mt: 1 }}
        >
          {t('modal.item_ask_more_info.accept_files')}
        </Typography>

        {!!error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 1 }}
          >
            {error}
          </Typography>
        )}
      </Stack>

      <HiddenFile
        reference={imageRef}
        accept="image/jpeg, image/png, image/gif, image/jpg, application/pdf"
        onChange={handleAddFile}
      />
    </>
  );
};

export default UploadButton;
